import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LanguageContext = createContext();

export const routeMap = {
  '/': { da: '/', en: '/en', de: '/de' },
  '/service': { da: '/service', en: '/en/service', de: '/de/service' },
  '/teknisk-radgivning': { da: '/teknisk-radgivning', en: '/en/technical-advisory', de: '/de/technische-beratung' },
  '/salg-og-kob': { da: '/salg-og-kob', en: '/en/sales-purchase', de: '/de/verkauf-kauf' },
  '/ecm-certifikat': { da: '/ecm-certifikat', en: '/en/ecm-certificate', de: '/de/ecm-zertifikat' },
  '/kontakt': { da: '/kontakt', en: '/en/contact', de: '/de/kontakt' }
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Detect language from current URL path
  const detectLanguageFromPath = (pathname) => {
    if (pathname.startsWith('/en/') || pathname === '/en') return 'en';
    if (pathname.startsWith('/de/') || pathname === '/de') return 'de';
    return 'da';
  };

  const [language, setLanguageState] = useState(() => {
    return detectLanguageFromPath(window.location.pathname);
  });

  // Sync language state when URL path changes
  useEffect(() => {
    const detected = detectLanguageFromPath(location.pathname);
    if (detected !== language) {
      setLanguageState(detected);
    }
  }, [location.pathname, language]);

  // Update HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Helper to get localized path for a Danish path
  const getLocalizedPath = (path, lang = language) => {
    const cleanPath = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
    const route = routeMap[cleanPath];
    if (route) {
      return route[lang] || route['da'];
    }
    return path;
  };

  // Helper to get route key (Danish path) from any localized path
  const getRouteKeyFromPath = (pathname) => {
    const cleanPath = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
    for (const [key, langs] of Object.entries(routeMap)) {
      if (Object.values(langs).includes(cleanPath)) {
        return key;
      }
    }
    return null;
  };

  // Function to change language and navigate to the corresponding localized URL
  const changeLanguage = (newLang) => {
    const currentRouteKey = getRouteKeyFromPath(location.pathname);
    if (currentRouteKey) {
      const targetPath = routeMap[currentRouteKey][newLang];
      if (targetPath) {
        navigate(targetPath);
        setLanguageState(newLang);
        return;
      }
    }
    
    // Fallback if current path is not in routeMap (e.g. 404 or unknown path)
    const targetPath = newLang === 'da' ? '/' : `/${newLang}`;
    navigate(targetPath);
    setLanguageState(newLang);
  };

  const t = (translations) => {
    if (!translations) return '';
    return translations[language] || translations['da'] || '';
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t, getLocalizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
